import { useState, useCallback, useEffect, useRef } from 'react';
import { QueueJob, QueueState, QueueStatus } from '../types/queue';
import { getQueueState, updateQueueJob, cancelQueueJob } from '../api/queueApi';

export interface UsePromptQueueReturn {
  /** All pending jobs (QUEUED, PAUSED) — excludes CURRENT and terminal jobs */
  queuedJobs: QueueJob[];
  /** The job currently being edited by this user (local state only — not shared with other users) */
  editingJob: QueueJob | null;
  /** Refetch queue state from the server */
  refresh: () => void;
  /**
   * Mark a job as being edited locally.
   * Returns the job so the caller can pre-fill the ChatInput.
   * Edit state is per-user and not shared — other users continue to see the queue running normally.
   */
  startEditing: (jobId: string) => Promise<QueueJob | null>;
  /**
   * Save edits: update text, settings, and model on the server. Clears local editing state.
   */
  saveEdit: (
    jobId: string,
    newText: string,
    settings?: Record<string, boolean>,
    modelPreference?: string,
    fileIds?: string[],
    artifactIds?: string[],
    imageIds?: string[],
    attachments?: any[],
    interactionMode?: string
  ) => Promise<void>;
  /**
   * Abort an in-progress edit without saving. Clears local editing state only.
   */
  cancelEdit: (jobId: string) => Promise<void>;
  /**
   * Cancel (soft-delete) a job entirely.
   */
  cancelJob: (jobId: string) => Promise<void>;
  /**
   * Resumes all PAUSED jobs back to QUEUED status.
   */
  resumeAll: () => Promise<void>;
  /**
   * Called by ChatPage after a successful enqueue so the new job
   * immediately appears in the queue list without waiting for the next poll.
   */
  addOptimisticJob: (job: QueueJob) => void;
}

/**
 * Manages the prompt queue state for a project.
 *
 * - Fetches queue state on mount and after any mutation.
 * - Editing is local-only. editingJob is cleared if the job disappears from the queue.
 * - Listens for `queue_position_update` WebSocket events forwarded via the
 *   `wsMessage` prop to refresh positions in real time.
 */
export function usePromptQueue(
  projectId: string,
  wsMessage?: { type: string; data?: any } | null
): UsePromptQueueReturn {
  const [queuedJobs, setQueuedJobs] = useState<QueueJob[]>([]);
  const [editingJob, setEditingJob] = useState<QueueJob | null>(null);
  const refreshLockRef = useRef(false);
  const refreshPendingRef = useRef(false);

  // ── Fetch queue state from the server ────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!projectId) return;
    if (refreshLockRef.current) {
      // Coalesce instead of dropping: the backend emits queue events
      // back-to-back (queue_job_started + queue_position_update), so a
      // refresh requested mid-flight must run again afterwards or the final
      // state is whatever the possibly-stale first snapshot returned.
      refreshPendingRef.current = true;
      return;
    }
    refreshLockRef.current = true;

    try {
      do {
        refreshPendingRef.current = false;
        try {
          const state: QueueState = await getQueueState(projectId);

          // Jobs waiting to be run — the only ones shown in the banner.
          // The server returns QUEUED, PAUSED and CURRENT jobs.
          const nonTerminal = state.jobs.filter(
            (j) =>
              j.status === QueueStatus.QUEUED ||
              j.status === QueueStatus.PAUSED
          );

          // All job IDs the server knows about (including CURRENT ones that
          // are actively running). We use this to decide whether to evict an
          // optimistic placeholder.
          const allKnownIds = new Set(state.jobs.map((j) => j.job_id));

          setQueuedJobs((prev) => {
            // Retain any optimistic placeholders that were added by addOptimisticJob
            // but that the server has not yet acknowledged at all (not even as
            // CURRENT). This prevents a concurrent refresh — triggered by an
            // unrelated WebSocket event — from wiping out optimistic entries when
            // the DB write for the newly-queued job is still in-flight.
            // Once the server returns the real job (whether as QUEUED or CURRENT),
            // `allKnownIds` will contain the job_id and we drop the placeholder.
            const unacknowledgedOptimistic = prev.filter(
              (j) => (j as any)._isOptimistic === true && !allKnownIds.has(j.job_id)
            );
            return [...nonTerminal, ...unacknowledgedOptimistic];
          });

          // Editing is local-only — only clear it if the job was removed from the queue entirely.
          // Do NOT clear it on every refresh (WebSocket events call refresh frequently).
          setEditingJob((prev) => {
            if (!prev) return null;
            const jobStillExists = nonTerminal.find((j) => j.job_id === prev.job_id);
            return jobStillExists ? prev : null;
          });
        } catch (err) {
          console.warn('[usePromptQueue] Failed to fetch queue state:', err);
        }
      } while (refreshPendingRef.current);
    } finally {
      refreshLockRef.current = false;
    }
  }, [projectId]);

  // Initial fetch
  useEffect(() => {
    refresh();
  }, [refresh]);

  // ── React to WebSocket queue_position_update events ──────────────────────
  useEffect(() => {
    if (!wsMessage) return;
    if (
      wsMessage.type === 'queue_position_update' ||
      wsMessage.type === 'queue_job_completed' ||
      wsMessage.type === 'queue_job_started'
    ) {
      refresh();
    }
  }, [wsMessage, refresh]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const startEditing = useCallback(
    async (jobId: string): Promise<QueueJob | null> => {
      const job = queuedJobs.find((j) => j.job_id === jobId);
      if (!job) return null;

      // Editing is local-only — no server call, no blocking intervention set.
      setEditingJob(job);
      return job;
    },
    [queuedJobs]
  );

  const saveEdit = useCallback(
    async (
      jobId: string, 
      newText: string, 
      settings?: Record<string, boolean>,
      modelPreference?: string,
      fileIds?: string[],
      artifactIds?: string[],
      imageIds?: string[],
      attachments?: any[],
      interactionMode?: string
    ): Promise<void> => {
      try {
        await updateQueueJob(projectId, jobId, {
          user_message: newText,
          settings,
          model_preference: modelPreference,
          interaction_mode: interactionMode,
          file_ids: fileIds,
          artifact_ids: artifactIds,
          image_ids: imageIds,
          attachments,
        });
        setEditingJob(null);
        await refresh();
      } catch (err) {
        console.error('[usePromptQueue] Failed to save edit:', err);
      }
    },
    [projectId, refresh]
  );

  const cancelEdit = useCallback(
    async (_jobId: string): Promise<void> => {
      // Editing is local-only — just clear local state, no server call needed.
      setEditingJob(null);
    },
    []
  );

  const cancelJob = useCallback(
    async (jobId: string): Promise<void> => {
      // Optimistic removal
      setQueuedJobs((prev) => prev.filter((j) => j.job_id !== jobId));
      if (editingJob?.job_id === jobId) setEditingJob(null);

      try {
        await cancelQueueJob(projectId, jobId);
      } catch (err) {
        console.error('[usePromptQueue] Failed to cancel job:', err);
        // Restore on failure
        await refresh();
      }
    },
    [projectId, editingJob, refresh]
  );

  const addOptimisticJob = useCallback((job: QueueJob) => {
    setQueuedJobs((prev) => {
      // Don't add duplicates
      if (prev.find((j) => j.job_id === job.job_id)) return prev;
      // Stamp _isOptimistic so refresh() can identify and conditionally
      // preserve this entry while the server DB write is still in-flight.
      return [...prev, { ...job, _isOptimistic: true } as any];
    });
  }, []);

  const resumeAll = useCallback(async (): Promise<void> => {
    try {
      const { resumeAllQueueJobs } = await import('../api/queueApi');
      await resumeAllQueueJobs(projectId);
      await refresh();
    } catch (err) {
      console.error('[usePromptQueue] Failed to resume queue:', err);
    }
  }, [projectId, refresh]);

  return {
    queuedJobs,
    editingJob,
    refresh,
    startEditing,
    saveEdit,
    cancelEdit,
    cancelJob,
    resumeAll,
    addOptimisticJob,
  };
}
